import { useState, useEffect, useRef } from 'react';
import { ref, onValue, set, push, get } from 'firebase/database';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook for support chat operations
 * Handles real-time messaging, typing indicators, and chat management
 */
export function useSupportChat(chatUserId) {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState({ user: false, admin: false, adminName: '' });
  const [userInfo, setUserInfo] = useState(null);
  const [status, setStatus] = useState('active');
  const typingTimeoutRef = useRef(null);

  // Listen to messages
  useEffect(() => {
    if (!chatUserId) return;

    const messagesRef = ref(db, `support-chats/${chatUserId}/messages`);

    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const msgs = [];
      snapshot.forEach((child) => {
        msgs.push({ id: child.key, ...child.val() });
      });
      setMessages(
        msgs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      );
    });

    return unsubscribe;
  }, [chatUserId]);

  // Listen to typing status
  useEffect(() => {
    if (!chatUserId) return;

    const typingRef = ref(db, `support-chats/${chatUserId}/typing`);

    const unsubscribe = onValue(typingRef, (snapshot) => {
      setTyping(snapshot.val() || { user: false, admin: false, adminName: '' });
    });

    return unsubscribe;
  }, [chatUserId]);

  // Listen to user info
  useEffect(() => {
    if (!chatUserId) return;

    const userInfoRef = ref(db, `support-chats/${chatUserId}/userInfo`);

    const unsubscribe = onValue(userInfoRef, (snapshot) => {
      setUserInfo(snapshot.val());
    });

    return unsubscribe;
  }, [chatUserId]);

  // Listen to status
  useEffect(() => {
    if (!chatUserId) return;

    const statusRef = ref(db, `support-chats/${chatUserId}/status`);

    const unsubscribe = onValue(statusRef, (snapshot) => {
      setStatus(snapshot.val() || 'active');
    });

    return unsubscribe;
  }, [chatUserId]);

  const sendMessage = async (text, isAdmin = false) => {
    if (!chatUserId || !currentUser) return;

    // Ensure userInfo exists before sending message
    const userInfoRef = ref(db, `support-chats/${chatUserId}/userInfo`);
    const userInfoSnapshot = await get(userInfoRef);

    if (!userInfoSnapshot.exists()) {
      // Create userInfo if it doesn't exist
      await set(userInfoRef, {
        email: currentUser.email,
        displayName: currentUser.displayName || 'User',
        photoURL: currentUser.photoURL || null,
        lastMessageAt: new Date().toISOString(),
      });
    }

    const messagesRef = ref(db, `support-chats/${chatUserId}/messages`);
    const newMessageRef = push(messagesRef);

    await set(newMessageRef, {
      senderId: currentUser.uid,
      senderName: currentUser.displayName || (isAdmin ? 'Admin' : 'User'),
      message: text,
      timestamp: new Date().toISOString(),
      isAdmin: isAdmin,
    });

    // Update lastMessageAt
    const lastMessageRef = ref(
      db,
      `support-chats/${chatUserId}/userInfo/lastMessageAt`
    );
    await set(lastMessageRef, new Date().toISOString());

    // If admin sent message, reset unreadByUser; if user sent, increment unreadByAdmin
    if (isAdmin) {
      const unreadUserRef = ref(db, `support-chats/${chatUserId}/unreadByUser`);
      const currentUnread = (await get(unreadUserRef)).val() || 0;
      await set(unreadUserRef, currentUnread + 1);

      // Reset admin unread counter
      const unreadAdminRef = ref(db, `support-chats/${chatUserId}/unreadByAdmin`);
      await set(unreadAdminRef, 0);
    } else {
      const unreadAdminRef = ref(db, `support-chats/${chatUserId}/unreadByAdmin`);
      const currentUnread = (await get(unreadAdminRef)).val() || 0;
      await set(unreadAdminRef, currentUnread + 1);
    }

    // Clear typing status
    const typingKey = isAdmin ? 'admin' : 'user';
    const typingRef = ref(db, `support-chats/${chatUserId}/typing/${typingKey}`);
    await set(typingRef, false);
  };

  const updateTypingStatus = async (isTyping, isAdmin = false) => {
    if (!chatUserId) return;

    const typingKey = isAdmin ? 'admin' : 'user';
    const typingRef = ref(db, `support-chats/${chatUserId}/typing/${typingKey}`);

    await set(typingRef, isTyping);

    if (isAdmin && isTyping && currentUser?.displayName) {
      const adminNameRef = ref(db, `support-chats/${chatUserId}/typing/adminName`);
      await set(adminNameRef, currentUser.displayName);
    }

    if (isTyping) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        set(typingRef, false);
      }, 3000);
    }
  };

  const markAsRead = async (isAdmin = false) => {
    if (!chatUserId) return;

    const unreadKey = isAdmin ? 'unreadByAdmin' : 'unreadByUser';
    const unreadRef = ref(db, `support-chats/${chatUserId}/${unreadKey}`);
    await set(unreadRef, 0);
  };

  const resolveChat = async () => {
    if (!chatUserId || !currentUser) return;

    const statusRef = ref(db, `support-chats/${chatUserId}/status`);
    const resolvedAtRef = ref(db, `support-chats/${chatUserId}/resolvedAt`);
    const resolvedByRef = ref(db, `support-chats/${chatUserId}/resolvedBy`);

    await set(statusRef, 'resolved');
    await set(resolvedAtRef, new Date().toISOString());
    await set(resolvedByRef, currentUser.email);
  };

  const initializeChatInfo = async () => {
    if (!chatUserId || !currentUser) return;

    const userInfoRef = ref(db, `support-chats/${chatUserId}/userInfo`);
    const userInfoSnapshot = await get(userInfoRef);

    if (!userInfoSnapshot.exists()) {
      await set(userInfoRef, {
        email: currentUser.email,
        displayName: currentUser.displayName || 'User',
        photoURL: currentUser.photoURL || null,
        lastMessageAt: new Date().toISOString(),
      });
    }
  };

  return {
    messages,
    typing,
    userInfo,
    status,
    sendMessage,
    updateTypingStatus,
    markAsRead,
    resolveChat,
    initializeChatInfo,
  };
}
